import React, { useEffect, useMemo, useState } from "react";
import { X, Video, Copy, Send, ExternalLink, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";

function nuovoLinkJitsi() {
  const parte = () => Math.random().toString(36).slice(2, 7);
  return `https://meet.jit.si/PostaAI-${parte()}-${parte()}`;
}
const pad = (n) => String(n).padStart(2, "0");
const gcalDate = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;

// Video call: Google Meet (avvia ora o programma su Google Calendar con Meet) oppure stanza Jitsi senza account
export default function VideoCallModal({ open, onClose, onInvia, onMemoChange, initial }) {
  const [tab, setTab] = useState("meet");
  const [titolo, setTitolo] = useState("Video call");
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [ora, setOra] = useState("10:00");
  const [durata, setDurata] = useState(30);
  const [invitati, setInvitati] = useState("");
  const [sugg, setSugg] = useState([]);
  const jitsi = useMemo(() => (open ? nuovoLinkJitsi() : ""), [open]);
  // apertura da un'email: titolo = oggetto, invitato = mittente
  useEffect(() => {
    if (!open) return;
    setTab("meet");
    setTitolo(initial?.titolo || "Video call");
    setInvitati(initial?.invitati || "");
    setSugg([]);
  }, [open, initial]);
  // suggerimenti dalla rubrica sull'ultimo indirizzo che si sta digitando
  useEffect(() => {
    const ultimo = invitati.split(/[,;\s]+/).pop() || "";
    if (ultimo.length < 2) { setSugg([]); return; }
    const t = setTimeout(() => api.cercaContatti(ultimo).then((r) => setSugg(r.slice(0, 6))).catch(() => setSugg([])), 180);
    return () => clearTimeout(t);
  }, [invitati]);
  const aggiungiInvitato = (email) => {
    const parti = invitati.split(/[,;\s]+/).filter(Boolean);
    parti.pop();
    if (!parti.includes(email)) parti.push(email);
    setInvitati(parti.join(", ") + ", ");
    setSugg([]);
  };
  if (!open) return null;

  const copia = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copiato"); } catch (e) { toast.error("Copia non riuscita"); }
  };

  const programmaMeet = async () => {
    const inizio = new Date(`${data}T${ora}:00`);
    if (isNaN(inizio)) return toast.error("Data o ora non valide");
    const fine = new Date(inizio.getTime() + durata * 60000);
    const guests = invitati.split(/[,;\s]+/).filter(Boolean).join(",");
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(titolo)}&dates=${gcalDate(inizio)}/${gcalDate(fine)}` +
      `&details=${encodeURIComponent("Video call organizzata da POSTA AI. Il link Google Meet viene aggiunto all'evento.")}` + (guests ? `&add=${encodeURIComponent(guests)}` : "") + "&vcon=meet&crm=AVAILABLE";
    window.open(url, "_blank", "noopener");
    try {
      await api.addMemo({ data, tipo: "appuntamento", testo: `Google Meet: ${titolo}${guests ? " con " + guests : ""}`, ora, account_id: initial?.account_id || "" });
      onMemoChange?.();
      toast.success("Evento aperto su Google Calendar e memo salvato nel calendario dell'app");
    } catch (e) {
      console.debug("VideoCall: memo facoltativo non salvato", e);
    }
    if (initial?.mittente_email) {
      // Meet da un'email: invito al mittente già pronto da inviare
      const quando = `${inizio.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} alle ${ora} (${durata} min)`;
      onInvia({
        a: initial.mittente_email,
        account_id: initial.account_id || "",
        oggetto: `Invito Google Meet: ${titolo}`,
        corpo: `Ciao ${initial.mittente || ""},\n\nti invito a una video call su Google Meet ${quando}.\nRiceverai l'invito con il link Meet da Google Calendar.\n\nA presto`,
      });
    }
  };

  const testoJitsi = "Ciao,\n\nti invito a una video call.\n\nPartecipa da qui: " + jitsi + "\n\nBasta aprire il link dal browser, senza registrazione.\n";

  return (
    <div className="wf-modal-backdrop show" onClick={onClose}>
      <div className="wf-modal modal-leggero videocall-modal" onClick={(e) => e.stopPropagation()} data-testid="videocall-modal">
        <div className="wf-modal-head">
          <span className="wf-modal-title"><Video size={20} strokeWidth={2.4} /> Video call</span>
          <button className="icon-btn" onClick={onClose} aria-label="Chiudi" data-testid="videocall-close"><X size={20} strokeWidth={2.6} /></button>
        </div>
        <div className="proto-row" style={{ marginBottom: 10 }}>
          <button className={`wf-btn proto-btn ${tab === "meet" ? "primary" : ""}`} onClick={() => setTab("meet")} data-testid="videocall-tab-meet">Google Meet</button>
          <button className={`wf-btn proto-btn ${tab === "jitsi" ? "primary" : ""}`} onClick={() => setTab("jitsi")} data-testid="videocall-tab-jitsi">Stanza senza account (Jitsi)</button>
        </div>
        {tab === "meet" ? (
          <div data-testid="videocall-meet">
            <a className="wf-btn send-btn" href="https://meet.google.com/new" target="_blank" rel="noopener noreferrer" data-testid="meet-now" style={{ justifyContent: "center" }}>
              <ExternalLink size={16} strokeWidth={2.3} /> Avvia un Meet adesso
            </a>
            <div className="calendario-lista-title" style={{ marginTop: 16 }}>Programma un Meet</div>
            <div className="meet-form">
            <input className="wf-input" value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Titolo" data-testid="meet-title" />
            <div className="host-port">
              <input className="wf-input" type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="meet-date" />
              <input className="wf-input" type="time" value={ora} onChange={(e) => setOra(e.target.value)} data-testid="meet-time" />
              <select className="wf-input" value={durata} onChange={(e) => setDurata(parseInt(e.target.value, 10))} data-testid="meet-duration">
                {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
            <div className="meet-guests-wrap">
              <input className="wf-input" value={invitati} onChange={(e) => setInvitati(e.target.value)} placeholder="Invitati (email separate da virgola)" autoComplete="off" data-testid="meet-guests" />
              {sugg.length > 0 && (
                <div className="meet-sugg" data-testid="meet-sugg">
                  {sugg.map((c) => (
                    <button key={c.email} type="button" className="meet-sugg-item" onMouseDown={(e) => e.preventDefault()} onClick={() => aggiungiInvitato(c.email)} data-testid={`meet-sugg-${c.email}`}>
                      <b>{c.nome || c.email}</b>{c.nome ? <span> · {c.email}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
            </div>
            <div className="wf-modal-actions videocall-actions">
              <button className="wf-btn primary" onClick={programmaMeet} data-testid="meet-schedule">
                <CalendarPlus size={16} strokeWidth={2.3} /> Crea su Google Calendar con Meet
              </button>
            </div>
            <p className="videocall-hint">Si apre Google Calendar con l'evento precompilato (invitati inclusi): salvandolo Google aggiunge il link Meet e invia gli inviti. Il promemoria viene anche salvato nel calendario dell'app.</p>
          </div>
        ) : (
          <div data-testid="videocall-jitsi">
            <p className="videocall-hint">Stanza creata. Invia il link agli invitati: potranno entrare dal browser, senza account.</p>
            <div className="videocall-link" data-testid="videocall-link">{jitsi}</div>
            <div className="wf-modal-actions videocall-actions">
              <button className="wf-btn" onClick={() => copia(jitsi)} data-testid="videocall-copy"><Copy size={16} strokeWidth={2.3} /> Copia link</button>
              <a className="wf-btn" href={jitsi} target="_blank" rel="noopener noreferrer" data-testid="videocall-open"><ExternalLink size={16} strokeWidth={2.3} /> Avvia ora</a>
              <button className="wf-btn send-btn" onClick={() => onInvia({ a: "", oggetto: "Invito alla video call", corpo: testoJitsi })} data-testid="videocall-send">
                <Send size={16} strokeWidth={2.3} /> Invia via email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
